import React, { useEffect, useRef, useState } from 'react'
import EditIcon from '@precis-digital/kurama/icons/EditIcon'
import { styled } from '@precis-digital/kurama/utils'
import FileUpload, { type FileUploadProps } from '@precis-digital/kurama/FileUpload'
import { AvatarWithLabel } from 'shared/components/AvatarWithLabel'
import { type Client } from 'shared/context/ClientContext'
import { type User } from 'shared/context/AuthContext'
import { useTranslation } from 'shared/translations'
import {
  handleAvatarUploadClick,
  validateFile,
  clearFileInput,
  handleValidationError,
  ACCEPTED_FILE_TYPES_STRING
} from './utils'
import { makeToastWithLoading } from 'shared/components/Toaster'
import { type RequireAtLeastOne } from 'type-fest'

interface StandaloneAvatarUploadProps {
  client?: Omit<Client, 'featurePlan' | 'currency'>
  user?: User
  uploadAvatarProps: {
    uploadAvatar: (file: File) => Promise<void>
    isUploading: boolean
    isUploadSuccess: boolean
    onUploadSuccess?: () => void
  }
}

const StandaloneAvatarUpload: React.FC<RequireAtLeastOne<StandaloneAvatarUploadProps, 'client' | 'user'>> = ({
  client,
  uploadAvatarProps,
  user
}) => {
  const { t } = useTranslation('common')
  const { uploadAvatar, isUploadSuccess, onUploadSuccess } = uploadAvatarProps
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [avatarVersion, setAvatarVersion] = useState<number | undefined>(undefined)

  useEffect(() => {
    if (isUploadSuccess) {
      setAvatarVersion(Date.now())
    }
  }, [isUploadSuccess])

  const onUpload: FileUploadProps['onUpload'] = (files) => {
    if (files.length === 0) return

    const file = files[0]
    const validationError = validateFile(file, t)

    if (validationError !== null) {
      handleValidationError({
        error: validationError,
        t,
        fileInputRef
      })
      return
    }

    const { toastOnSuccess, toastOnError } = makeToastWithLoading({
      headerMessage: t('toasts.uploadingImage')
    })

    uploadAvatar(file)
      .then(() => {
        onUploadSuccess?.()
        toastOnSuccess(t('toasts.imageUploadSuccess'))
      })
      .catch(() => {
        toastOnError(t('toasts.imageUploadFailed'))
      })
      .finally(() => {
        clearFileInput(fileInputRef)
      })
  }

  const onButtonClick = (): void => {
    handleAvatarUploadClick({
      fileInputRef
    })
  }

  return (
    <>
      <FileUpload
        name="avatar"
        placeholder={t('avatarUpload.uploadAvatar')}
        maxFiles={1}
        multiple={false}
        onUpload={onUpload}
        InputProps={{ ref: fileInputRef, accept: ACCEPTED_FILE_TYPES_STRING }}
        style={{ display: 'none' }}
      />
      <AvatarContainer onClick={onButtonClick}>
        <AvatarWithLabel size="large" client={client} user={user} avatarVersion={avatarVersion} />
        <EditIconOverlay>
          <EditIcon />
        </EditIconOverlay>
      </AvatarContainer>
    </>
  )
}

export default StandaloneAvatarUpload

const AvatarContainer = styled('div')({
  position: 'relative',
  display: 'inline-block',
  cursor: 'pointer'
})

const EditIconOverlay = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: -2,
  right: 0,
  width: '30px',
  height: '30px',
  backgroundColor: theme.tokens.background.tonal.soft.default,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `2px solid ${theme.tokens.background.main.default}`,
  '& svg': {
    width: '14px',
    height: '14px',
    color: theme.tokens.foreground.main.primary
  }
}))
